"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useState, useCallback } from "react";
import { Drawer as Drawer$1, Box } from "@mui/material";
import { SpaceMd } from "@nxt-costco-com/forge-design-tokens";
import { DrawerHeader } from "./DrawerHeader/index.js";
import { TITLE_ID, DRAWER_WIDTH, BODY_ID, STICKYWRAPPER_ZINDEX, COMPONENT_NAME } from "./constants.js";
import { StickyWrapper } from "../StickyWrapper/index.js";
import { getDataTestId } from "../../Util/index.js";
const getBodyScrollSx = (headerHeight, stickyBarContent, stickyWrapperHeight) => {
  const baseHeight = stickyBarContent ? headerHeight + stickyWrapperHeight : headerHeight;
  return {
    overflowY: "auto",
    height: `calc(100vh - ${baseHeight}px)`,
    "@supports (height: 100dvh)": {
      height: `calc(100dvh - ${baseHeight}px)`
    }
  };
};
const Drawer = forwardRef(
  ({
    bodySx,
    open,
    anchor,
    PaperProps,
    sx,
    id,
    "aria-label": ariaLabel,
    children,
    uniqueId,
    scrollArea = "drawer",
    headerProps,
    stickyBarContent,
    stickyWrapperProps
  }, ref) => {
    const headerRef = useRef(null);
    const drawerRef = useRef(null);
    const stickyWrapperRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [stickyWrapperHeight, setStickyWrapperHeight] = useState(0);
    const calculateHeaderHeight = useCallback(() => {
      if (!headerRef.current) return;
      const newHeight = headerRef.current.offsetHeight;
      setHeaderHeight(
        (prev) => newHeight > 0 && newHeight !== prev ? newHeight : prev
      );
    }, []);
    const calculateStickyWrapperHeight = useCallback(() => {
      if (!stickyWrapperRef.current) return;
      const newHeight = stickyWrapperRef.current.offsetHeight;
      setStickyWrapperHeight(
        (prev) => newHeight > 0 && newHeight !== prev ? newHeight : prev
      );
    }, []);
    const { title, onClose } = headerProps || {};
    const isBodyScroll = scrollArea === "body";
    return /* @__PURE__ */ jsxs(
      Drawer$1,
      {
        PaperProps: {
          role: "dialog",
          "aria-modal": true,
          ...!title && { "aria-label": ariaLabel },
          ...!!title && { "aria-labelledby": TITLE_ID },
          sx: { overflowY: "unset" },
          ref: isBodyScroll ? null : drawerRef,
          ...PaperProps
        },
        open,
        anchor,
        onClose,
        onTransitionEnd: (e) => {
          if (isBodyScroll && e.propertyName === "transform" && open) {
            calculateHeaderHeight();
          }
          if (stickyBarContent) {
            calculateStickyWrapperHeight();
          }
        },
        sx,
        id,
        ref,
        "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
        children: [
          /* @__PURE__ */ jsxs(
            Box,
            {
              sx: {
                width: DRAWER_WIDTH,
                bottom: "auto",
                height: "100vh",
                overflowY: !isBodyScroll ? "auto" : "visible"
              },
              children: [
                /* @__PURE__ */ jsx(DrawerHeader, { ...headerProps, ref: headerRef }),
                /* @__PURE__ */ jsx(
                  Box,
                  {
                    "data-testid": BODY_ID,
                    ...isBodyScroll ? {
                      role: "region",
                      ref: drawerRef,
                      ...!!title && {
                        "aria-labelledby": TITLE_ID
                      }
                    } : {},
                    sx: [
                      {
                        paddingBlock: SpaceMd,
                        paddingInline: SpaceMd
                      },
                      ...isBodyScroll ? [
                        getBodyScrollSx(
                          headerHeight,
                          Boolean(stickyBarContent),
                          stickyWrapperHeight
                        )
                      ] : [],
                      ...Array.isArray(bodySx) ? bodySx : [bodySx]
                    ],
                    children
                  }
                )
              ]
            }
          ),
          stickyBarContent && /* @__PURE__ */ jsx(
            StickyWrapper,
            {
              width: DRAWER_WIDTH,
              stickyPosition: "bottom",
              extraPaddingBottom: false,
              hasTopButton: false,
              isMobile: true,
              zIndex: STICKYWRAPPER_ZINDEX,
              scrollElement: drawerRef,
              ref: stickyWrapperRef,
              ...stickyWrapperProps,
              children: stickyBarContent
            }
          )
        ]
      }
    );
  }
);
Drawer.displayName = COMPONENT_NAME;
export {
  Drawer as DrawerV2
};
